import { calculateForecastAmount } from '../../forecasting';
import {
    CurrentRateInfo,
    FinancialEntitySnapshotFieldsFragment,
} from '../../graphql/generated';
import { TransactionSchedulePlan } from '../../types';
import { getEarliestDate } from '../../util/dateTime';
import {
    endOfTickDateStr,
    GoalValueNode,
    makeValueSeriesGenerator,
    Tick,
} from '../../valueSeries';

export type GoalSnapshot = FinancialEntitySnapshotFieldsFragment;

export interface VSGFactoryGoalParams {
    id: string;
    targetAmount: number | null | undefined;
    /* fixed date for targeted goals, floating date for untargeted goals */
    targetDate: string | null | undefined;
    name: string;
    color: string;
    currentRateInfo: CurrentRateInfo;
}

export interface VSGFactoryParticipantParams {
    annualReturn: number;
    currentValue: number;
    snapshots: GoalSnapshot[];
    transactionSchedulePlans: TransactionSchedulePlan[] | undefined;
}

export interface VSGFactoryOptionsParams {
    forecastCompletionDate?: string;
    useForecastForCurrent?: boolean;
}

export const getGoalAccountVSG = ({
    owner: { annualReturn, currentValue, snapshots, transactionSchedulePlans },
    opts: { forecastCompletionDate, useForecastForCurrent },
    currentDate,
    valueNodeBaseParams,
    maybeCapBalance,
}: {
    owner: VSGFactoryParticipantParams;
    opts: VSGFactoryOptionsParams;
    currentDate: string;
    valueNodeBaseParams: { goalId: string; name: string; color: string };
    maybeCapBalance: (
        tick: Tick,
        balance: number,
        currentValue: number
    ) => number | null;
}) => {
    const getForecastValue = (tick: Tick) => {
        const forecastValue = calculateForecastAmount({
            startAmount: currentValue,
            annualGrowthRate: annualReturn,
            transactionSchedulePlans,
            startDate: currentDate,
            targetDate: getEarliestDate(
                endOfTickDateStr(tick),
                forecastCompletionDate
            ),
        });
        return maybeCapBalance(tick, forecastValue, currentValue);
    };

    return makeValueSeriesGenerator<GoalSnapshot, GoalValueNode>({
        snapshots,
        currentDate,
        generatePastValueNode: ({ tick, snapshot }) => {
            const balance = snapshot?.balance ?? null;
            return { ...valueNodeBaseParams, ...tick, balance };
        },
        generateCurrentValueNode: ({ tick }) => {
            // N.B. We don't currently set useForecastForCurrent = true
            // anywhere in the codebase, but leaving this in while we're rapidly
            // experimenting with this experience to facilitate iteration.
            const balance = useForecastForCurrent
                ? getForecastValue(tick)
                : currentValue;
            return { ...valueNodeBaseParams, ...tick, balance };
        },
        generateFutureValueNode: ({ tick }) => {
            const balance = getForecastValue(tick);
            return { ...valueNodeBaseParams, ...tick, balance };
        },
    });
};

export const getCappedBalance = ({
    balance,
    currentValue,
    targetAmount,
}: {
    balance: number;
    currentValue: number;
    targetAmount: number;
}) => {
    // Cap projected balance at max = targetAmount, but min = currentValue
    const cappedBalance = Math.max(
        currentValue,
        Math.min(balance, targetAmount)
    );

    return cappedBalance;
};
